import { useEffect, useMemo, useState } from 'react';

import { executeHttpRequest } from '../../../epics/api';
import { TExtraRate } from './types';

export type TTimesheetRejection = {
    created: string;
    reason: string;
};

export type TTimesheetPayRate = {
    id?: number;
    rate_code: string;
    description: string;
    duration: string;
    freelancer_pay_per_hour: string;
    client_pay_per_hour: string;
    adhoc_pay_rate: boolean;
};

export type TTimesheetDetails = {
    id: number;
    status: string;
    timesheet_number: number | string;
    status_display: string;
    last_approval_email_sent_at?: string | null;
    timesheet_date: string;
    job_description: string;
    client_name: string;
    agent_name: string;
    freelancer_name: string;
    personnel_reference: string;
    client_reference: string;
    consultant_code: string;
    employer_reference: string;
    division_reference: string;
    department_reference: string;
    po_number: string;
    hours_worked: number;
    pay_rate: string;
    charge_rate: string;
    pay_rates: TTimesheetPayRate[];
    rejections: TTimesheetRejection[];
    is_cancellable?: boolean;
};

const EMPTY_TIMESHEET_DETAILS: TTimesheetDetails = {
    id: 0,
    status: '',
    timesheet_number: '',
    status_display: '',
    last_approval_email_sent_at: null,
    timesheet_date: '',
    job_description: '',
    client_name: '',
    agent_name: '',
    freelancer_name: '',
    personnel_reference: '',
    client_reference: '',
    consultant_code: '',
    employer_reference: '',
    division_reference: '',
    department_reference: '',
    po_number: '',
    hours_worked: 0,
    pay_rate: '',
    charge_rate: '',
    pay_rates: [],
    rejections: [],
    is_cancellable: false,
};

type TTimesheetFormState = ReturnType<typeof buildInitialState>;

type UseTimesheetDetailsParams = {
    isOpen: boolean;
    timesheetId?: number | null;
    allowEdit: boolean;
    notification?: { success: (title: string, message: string) => void; error?: (title: string, message: string) => void };
    onSaved?: () => void;
    onClose: () => void;
};

const buildInitialState = (data: TTimesheetDetails) => ({
    timesheet_date: data.timesheet_date ?? '',
    job_description: data.job_description ?? '',
    personnel_reference: data.personnel_reference ?? '',
    client_reference: data.client_reference ?? '',
    consultant_code: data.consultant_code ?? '',
    employer_reference: data.employer_reference ?? '',
    division_reference: data.division_reference ?? '',
    department_reference: data.department_reference ?? '',
    po_number: data.po_number ?? '',
    hours_worked: data.hours_worked ?? '',
    pay_rate: data.pay_rate ?? '',
    charge_rate: data.charge_rate ?? '',
    pay_rates: data.pay_rates ?? [],
});

const getNextAdhocId = (payRates: TTimesheetPayRate[] | undefined) => {
    const numericIds = (payRates || [])
        .map(rate => Number(rate.id))
        .filter(id => Number.isFinite(id));
    const maxExistingId = numericIds.length ? Math.max(...numericIds) : 0;
    return maxExistingId + 1;
};

const parseDurationToHours = (duration?: string | number): number => {
    if (typeof duration === 'number') {
        return duration;
    }
    if (!duration) {
        return 0;
    }
    const parts = duration.split(':').map(part => Number(part));
    if (parts.length === 3) {
        const [h, m, s] = parts;
        const minutes = (m || 0) / 60;
        const seconds = (s || 0) / 3600;
        return h + minutes + seconds;
    }
    const asNumber = Number(duration);
    return Number.isNaN(asNumber) ? 0 : asNumber;
};

const toDurationString = (duration: string | number | undefined): string => {
    if (duration === undefined || duration === null || duration === '') {
        return '';
    }
    if (typeof duration === 'string' && duration.includes(':')) {
        return duration;
    }
    const hours = typeof duration === 'number' ? duration : Number(duration);
    if (Number.isNaN(hours)) {
        return '';
    }
    const totalSeconds = Math.round(hours * 3600);
    const h = Math.floor(totalSeconds / 3600)
        .toString()
        .padStart(2, '0');
    const remainderAfterHours = totalSeconds % 3600;
    const m = Math.floor(remainderAfterHours / 60)
        .toString()
        .padStart(2, '0');
    const s = Math.floor(remainderAfterHours % 60)
        .toString()
        .padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const toExtraRate = (rate: TTimesheetPayRate): TExtraRate => ({
    id: rate.id as number,
    extraRateCode: rate.rate_code || '',
    description: rate.description || '',
    duration: parseDurationToHours(rate.duration),
    freelancerPayPerHour: Number(rate.freelancer_pay_per_hour ?? 0),
    clientPayPerHour: Number(rate.client_pay_per_hour ?? 0),
    jobReference: '',
    jobRequestId: 0,
});

const splitPayRates = (payRates: TTimesheetPayRate[]) => {
    const baseRates: TExtraRate[] = [];
    const additionalRates: TExtraRate[] = [];

    (payRates || []).forEach(rate => {
        const normalized = toExtraRate(rate);
        if (rate.adhoc_pay_rate) {
            additionalRates.push(normalized);
        } else {
            baseRates.push(normalized);
        }
    });

    return { baseRates, additionalRates };
};

export const useTimesheetDetails = ({
    isOpen,
    timesheetId,
    allowEdit,
    notification,
    onSaved,
    onClose,
}: UseTimesheetDetailsParams) => {
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState<TTimesheetDetails | null>(null);
    const [formState, setFormState] = useState<TTimesheetFormState>(buildInitialState(EMPTY_TIMESHEET_DETAILS));
    const [isEditing, setIsEditing] = useState(false);
    const [nextAdhocId, setNextAdhocId] = useState(() => getNextAdhocId(EMPTY_TIMESHEET_DETAILS.pay_rates));
    const [isGenerating, setIsGenerating] = useState(false);
    const [isResendingApprovalEmail, setIsResendingApprovalEmail] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    const isCancelled = details?.status === 'CAN';
    const canEdit = allowEdit || isCancelled;
    const effectiveIsEditing = canEdit && isEditing;
    const canResendApprovalEmail = details?.status === 'AWA';
    const isCancellable = details?.is_cancellable ?? false;

    useEffect(() => {
        if (!isOpen || !timesheetId) {
            return;
        }
        setLoading(true);
        executeHttpRequest(`timesheets/${timesheetId}/`, 'GET')
            .then(response => {
                const data: TTimesheetDetails = response?.data.content || {};
                setDetails(data);
                setFormState(buildInitialState(data));
                setNextAdhocId(getNextAdhocId(data.pay_rates));
            })
            .catch(() => {
                notification?.error?.('Error', 'Failed to load timesheet details');
            })
            .finally(() => setLoading(false));
    }, [isOpen, timesheetId]);

    const { baseRates, additionalRates } = useMemo(
        () => splitPayRates(formState.pay_rates || []),
        [formState.pay_rates],
    );

    const lastThreeRejections = useMemo(
        () => (details?.rejections || []).slice(-3).reverse(),
        [details?.rejections],
    );

    const handleDescriptionChange = (value: string) => {
        setFormState(prev => ({
            ...prev,
            job_description: value,
        }));
    };

    const handleUpdateRate = (
        rateId: number,
        updater: (rate: TExtraRate) => TExtraRate,
    ) => {
        setFormState(prev => {
            const nextRates = (prev.pay_rates || []).map(rate => {
                if (rate.id !== rateId) {
                    return rate;
                }
                const updatedExtraRate = updater(toExtraRate(rate));
                return {
                    ...rate,
                    rate_code: updatedExtraRate.extraRateCode,
                    description: updatedExtraRate.description,
                    duration: updatedExtraRate.duration?.toString() || '',
                    freelancer_pay_per_hour: updatedExtraRate.freelancerPayPerHour?.toString() || '',
                    client_pay_per_hour: updatedExtraRate.clientPayPerHour?.toString() || '',
                    adhoc_pay_rate: rate.adhoc_pay_rate,
                };
            });
            return { ...prev, pay_rates: nextRates };
        });
    };

    const handleToggleEdit = () => setIsEditing(prev => !prev);

    const handleToggleEditGuarded = () => {
        if (!canEdit) return;
        handleToggleEdit();
    };

    const handleAddAdditionalRate = () => {
        const newRate: TTimesheetPayRate = {
            id: nextAdhocId,
            rate_code: '',
            description: '',
            duration: '',
            freelancer_pay_per_hour: '',
            client_pay_per_hour: '',
            adhoc_pay_rate: true,
        };
        setFormState(prev => ({
            ...prev,
            pay_rates: [
                ...(prev.pay_rates || []),
                newRate,
            ],
        }));
        setNextAdhocId(id => id + 1);
        setIsEditing(true);
    };

    const handleRemoveRate = (rateId: number) => {
        setFormState(prev => {
            const nextRates = (prev.pay_rates || []).filter(rate => rate.id !== rateId);
            return { ...prev, pay_rates: nextRates };
        });
    };

    const handleSave = () => {
        if (!timesheetId || !canEdit) return;
        if (!formState.job_description.trim()) {
            notification?.error?.('Validation', 'Description is required.');
            return;
        }
        const hasInvalidRates = (formState.pay_rates || []).some(rate => {
            const duration = parseDurationToHours(rate.duration);
            const pay = Number(rate.freelancer_pay_per_hour);
            const charge = Number(rate.client_pay_per_hour);
            const isMissingOrInvalid =
                !rate.rate_code?.trim() ||
                !rate.description?.trim() ||
                Number.isNaN(duration) ||
                duration <= 0 ||
                Number.isNaN(pay) ||
                pay <= 0 ||
                Number.isNaN(charge) ||
                charge <= 0;
            return isMissingOrInvalid;
        });
        if (hasInvalidRates) {
            notification?.error?.(
                'Validation',
                'Please fill rate code, description, duration, pay rate, and charge rate with values greater than 0.',
            );
            return;
        }
        setIsGenerating(true);
        const payload = {
            job_description: formState.job_description,
            pay_rates: (formState.pay_rates || []).map(rate => ({
                rate_code: rate.rate_code || '',
                description: rate.description || '',
                duration: toDurationString(rate.duration),
                freelancer_pay_per_hour: String(rate.freelancer_pay_per_hour ?? ''),
                client_pay_per_hour: String(rate.client_pay_per_hour ?? ''),
                adhoc_pay_rate: !!rate.adhoc_pay_rate,
            })),
        };

        executeHttpRequest(`timesheets/${timesheetId}/`, 'PATCH', payload)
            .then(() => {
                setIsEditing(false);
                notification?.success?.('Saved', 'Timesheet updated');
                onClose();
                onSaved?.();
            })
            .catch(() => {
                notification?.error?.('Error', 'Failed to save timesheet');
            })
            .finally(() => setIsGenerating(false));
    };

    const handleCancelTimesheet = (reason: string): Promise<boolean> => {
        if (!timesheetId) return Promise.resolve(false);
        setIsCancelling(true);
        return executeHttpRequest(`timesheets/${timesheetId}/cancel/`, 'POST', { reason })
            .then(response => {
                const updatedTimesheet: TTimesheetDetails = response?.data?.content;
                if (updatedTimesheet) {
                    setDetails({ ...updatedTimesheet, is_cancellable: false });
                    setFormState(buildInitialState(updatedTimesheet));
                }
                notification?.success?.('Success', 'Timesheet cancelled successfully');
                onSaved?.();
                return true;
            })
            .catch(error => {
                const message = error?.response?.data?.detail || 'Failed to cancel timesheet';
                notification?.error?.('Error', message);
                return false;
            })
            .finally(() => setIsCancelling(false));
    };

    const handleResendApprovalEmail = (): Promise<boolean> => {
        if (!timesheetId || !canResendApprovalEmail) {
            return Promise.resolve(false);
        }
        setIsResendingApprovalEmail(true);
        return executeHttpRequest(`timesheets/${timesheetId}/resend-approval-email/`, 'POST')
            .then(response => {
                const detailMessage = response?.data?.detail || 'Timesheet approval email resent to client approvers.';
                notification?.success?.('Success', detailMessage);
                return true;
            })
            .catch(() => {
                notification?.error?.('Error', 'Failed to resend approval email');
                return false;
            })
            .finally(() => setIsResendingApprovalEmail(false))
            .then(result => result);
    };

    return {
        loading,
        details,
        formState,
        baseRates,
        additionalRates,
        lastThreeRejections,
        canEdit,
        canResendApprovalEmail,
        effectiveIsEditing,
        isGenerating,
        isResendingApprovalEmail,
        isCancelling,
        isCancellable,
        isCancelled,
        lastApprovalEmailSentAt: details?.last_approval_email_sent_at ?? null,
        handleDescriptionChange,
        handleUpdateRate,
        handleAddAdditionalRate,
        handleRemoveRate,
        handleToggleEditGuarded,
        handleSave,
        handleResendApprovalEmail,
        handleCancelTimesheet,
    };
};

export const TimesheetDetailsDefaults = {
    EMPTY_TIMESHEET_DETAILS,
    buildInitialState,
    parseDurationToHours,
    toDurationString,
};
